import { defineAsyncComponent } from "vue";
import type { AuthorizationRuleForGatingInput } from "../use-gating-rule-conversion";
import type {
  CriterionApplyContext,
  GatingCriterionTypeDefinition,
} from "./gating-criterion-type";
import { GatingStrategyType } from "../strategies/gating-strategy";

/**
 * Membership is asymmetric: it uses a flat `channel_membership_id` field
 * rather than a nested criterion object like the other types.
 */
export const membershipCriterionType: GatingCriterionTypeDefinition = {
  presetId: "members_only",
  classificationKey: GatingStrategyType.membership,
  capabilityKey: "canUseMembershipGating",

  labelKey: "channel.gating.builder.presets.membersOnly.label",
  descriptionKey: "channel.gating.builder.presets.membersOnly.description",
  icon: "material-symbols:card-membership-outline",
  columnHeaderKey: "channel.gating.builder.featureRows.membershipTier",

  hasCriterion(rule: AuthorizationRuleForGatingInput): boolean {
    return !!rule.channel_membership_id;
  },

  isGated(
    rule: AuthorizationRuleForGatingInput,
    freeMembershipId: string | undefined,
  ): boolean {
    return !!(
      rule.channel_membership_id &&
      rule.channel_membership_id !== freeMembershipId
    );
  },

  applyDefaults(
    rule: AuthorizationRuleForGatingInput,
    context: CriterionApplyContext,
  ): void {
    rule.channel_membership_id ??= context.defaultMembershipId;
  },

  clearCriterion(rule: AuthorizationRuleForGatingInput): void {
    rule.channel_membership_id = undefined;
  },

  copyCriterion(
    source: AuthorizationRuleForGatingInput,
    target: AuthorizationRuleForGatingInput,
  ): void {
    target.channel_membership_id = source.channel_membership_id;
  },

  criterionEquals(
    a: AuthorizationRuleForGatingInput,
    b: AuthorizationRuleForGatingInput,
  ): boolean {
    return a.channel_membership_id === b.channel_membership_id;
  },

  serializeCriterion(rule: AuthorizationRuleForGatingInput): unknown {
    return rule.channel_membership_id;
  },

  expandedLayout: "inline",

  indeterminatePlaceholder: defineAsyncComponent(
    () =>
      import("../builder/components/membership-indeterminate-placeholder.vue"),
  ),

  expandedValueEditor: defineAsyncComponent(
    () => import("../builder/components/membership-expanded-editor.vue"),
  ),
};
